<template>
  <div class="flex flex-col gap-4">
    <!-- Gradient Toggle -->
    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <UISwitch id="gradient-toggle" :value="gradientEnabled" @update:value="toggleGradient" />
        <UITextSmMedium>{{ t("common.gradient") }}</UITextSmMedium>
      </div>
    </div>

    <!-- Gradient Configuration (shown when enabled) -->
    <div v-if="gradientEnabled" class="flex flex-col gap-5">
      <!-- Gradient Type Selection -->
      <div class="flex flex-col gap-2">
        <UITextSmMedium>{{ t("common.type") }}</UITextSmMedium>
        <UISelect
          id="gradient-type"
          v-model:value="gradientType"
          :options="[
            { label: t('common.linear'), value: 'linear' },
            { label: t('common.radial'), value: 'radial' },
            { label: t('common.angular'), value: 'conic' },
          ]"
          size="large"
          class="font-medium"
        />
      </div>

      <!-- Gradient Color Slider -->
      <div class="flex flex-col gap-2">
        <GradientColorSlider
          :colors="gradientColors"
          :gradient-type="gradientType"
          :gradient-angle="gradientAngle"
          @update:colors="gradientColors = $event"
          @add-color="addColor"
          @remove-color="removeColor"
          @update-color="updateColor"
        />
      </div>

      <!-- Angle Control (for linear and conic gradients) -->
      <div v-if="gradientType === 'linear' || gradientType === 'conic'" class="flex flex-col gap-2">
        <div class="flex items-center justify-start gap-1">
          <UITextSmMedium>{{ t("common.angle") }}</UITextSmMedium>
          <UITooltip>
            <template #trigger>
              <InfoCircleIcon class="h-3 w-3 text-gray-500" />
            </template>
            {{ t("common.angleTooltip") }}
          </UITooltip>
        </div>
        <div class="flex items-center gap-2">
          <UISlider
            id="gradient-angle-slider"
            v-model:value="gradientAngle"
            :min="0"
            :max="360"
            @mousedown="isSliderDragging = true"
            @touchstart="isSliderDragging = true"
            @mouseup="isSliderDragging = false"
            @touchend="isSliderDragging = false"
          />
          <UIInputNumber
            id="gradient-angle-input"
            :model-value="gradientAngle"
            size="large"
            :show-button="false"
            :precision="0"
            :min="0"
            :max="360"
            placeholder=""
            class="w-[100px]"
            @update:model-value="(value) => (gradientAngle = value || gradientAngle)"
          />
        </div>
      </div>
    </div>

    <!-- Solid Color Input (shown when gradient is disabled) -->
    <div v-if="!gradientEnabled" class="flex flex-col gap-3">
      <div class="flex flex-col gap-2">
        <UITextSmMedium class="full-opacity gradient-background__label text-gray-700">{{
          t("common.buttonBackground")
        }}</UITextSmMedium>
        <div
          class="flex items-center gap-2 rounded-lg border border-[#d0d5dd] bg-white py-2 px-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] [&_.n-color-picker]:!h-6 [&_.n-color-picker]:!w-6"
        >
          <UIColorPicker
            :value="formatColorForCSS(solidColor)"
            type="default"
            @update:value="updateSolidColor"
          />
          <UITextXsMedium class="color-hex">#{{ solidColor }}</UITextXsMedium>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash/debounce";
import { InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIColorPicker,
  UIInputNumber,
  UISelect,
  UISlider,
  UISwitch,
  UITextSmMedium,
  UITextXsMedium,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import {
  DEFAULT_GRADIENT_BACKGROUND_ANGLE,
  DEFAULT_GRADIENT_BACKGROUND_COLOR_STOPS,
  DEFAULT_GRADIENT_BACKGROUND_SOLID_FALLBACK_HEX,
  DEFAULT_GRADIENT_BACKGROUND_SOLID_PLACEHOLDER_HEX,
  DEFAULT_GRADIENT_BACKGROUND_TYPE,
} from "@/util/constants";
import GradientColorSlider from "./GradientColorSlider.vue";
import {
  ColorStop,
  GradientConfig,
  addColorStop,
  createDefaultGradientFromColor,
  formatColorForCSS,
  formatColorFromCSS,
  gradientToCss,
  parseGradientFromCss,
  removeColorStop,
  updateColorStop,
  validateGradientConfig,
} from "./GradientUtils";

interface Props {
  modelValue: string; // CSS string (gradient or solid color)
}

interface Emits {
  (e: "update:modelValue", value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();

// Reactive state
const gradientEnabled = ref(false);
const gradientType = ref<"linear" | "radial" | "conic">(DEFAULT_GRADIENT_BACKGROUND_TYPE);
const gradientAngle = ref(DEFAULT_GRADIENT_BACKGROUND_ANGLE);
const gradientColors = ref<ColorStop[]>([...DEFAULT_GRADIENT_BACKGROUND_COLOR_STOPS]);

const isSliderDragging = ref(false);

// Computed properties
const solidColor = computed(() => {
  if (gradientEnabled.value) {
    return DEFAULT_GRADIENT_BACKGROUND_SOLID_PLACEHOLDER_HEX;
  }

  // Extract solid color from CSS string
  if (props.modelValue && !props.modelValue.includes("gradient")) {
    return formatColorFromCSS(props.modelValue);
  }

  // If we have a gradient, extract the first color as fallback
  if (props.modelValue && props.modelValue.includes("gradient")) {
    const parsed = parseGradientFromCss(props.modelValue);
    if (parsed.colors.length > 0) {
      return formatColorFromCSS(parsed.colors[0].color);
    }
  }

  return DEFAULT_GRADIENT_BACKGROUND_SOLID_FALLBACK_HEX;
});

// Initialize from model value
const initializeFromModel = () => {
  if (!props.modelValue || props.modelValue === "none") {
    gradientEnabled.value = false;
    return;
  }

  if (props.modelValue.includes("gradient")) {
    // Parse gradient
    const parsed = parseGradientFromCss(props.modelValue);
    gradientEnabled.value = parsed.enabled;
    gradientType.value = parsed.type;
    gradientAngle.value = parsed.angle;
    gradientColors.value = parsed.colors;
  } else {
    // Solid color
    gradientEnabled.value = false;
  }
};

const emitModelUpdate = () => {
  if (gradientEnabled.value) {
    const config: GradientConfig = {
      enabled: true,
      type: gradientType.value,
      angle: gradientAngle.value,
      colors: gradientColors.value,
    };

    const validatedConfig = validateGradientConfig(config);
    const cssString = gradientToCss(validatedConfig);
    emit("update:modelValue", cssString);
  } else {
    const color = formatColorForCSS(solidColor.value);
    emit("update:modelValue", color);
  }
  isSliderDragging.value = false;
};

/** 50ms trailing debounce while dragging sliders; immediate emit when not dragging */
const debouncedEmitModelUpdate = debounce(emitModelUpdate, 50);

const scheduleModelUpdate = () => {
  if (isSliderDragging.value) {
    debouncedEmitModelUpdate();
  } else {
    debouncedEmitModelUpdate.cancel();
    emitModelUpdate();
  }
};

// Watch for changes and emit updates
watch(
  [gradientEnabled, gradientType, gradientAngle, gradientColors],
  () => {
    scheduleModelUpdate();
  },
  { deep: true }
);

// Watch for external model changes
watch(
  () => props.modelValue,
  () => {
    initializeFromModel();
  },
  { immediate: true }
);

// Methods
const toggleGradient = (value?: boolean) => {
  const newEnabledState = value !== undefined ? value : !gradientEnabled.value;
  gradientEnabled.value = newEnabledState;

  if (newEnabledState && gradientColors.value.length === 0) {
    // Initialize with default gradient based on current solid color
    const defaultGradient = createDefaultGradientFromColor(formatColorForCSS(solidColor.value));
    gradientType.value = defaultGradient.type;
    gradientAngle.value = defaultGradient.angle;
    gradientColors.value = defaultGradient.colors;
  }
};

const updateSolidColor = (value: string) => {
  if (!gradientEnabled.value) {
    // value is already in CSS format (#FFFFFF), so use it directly
    emit("update:modelValue", value);
  }
};

const addColor = (percentage?: number) => {
  gradientColors.value = addColorStop(gradientColors.value, percentage);
};

const removeColor = (id: number) => {
  gradientColors.value = removeColorStop(gradientColors.value, id);
};

const updateColor = (id: number, field: "color" | "stop", value: string) => {
  gradientColors.value = updateColorStop(gradientColors.value, id, field, value);
};

onUnmounted(() => {
  debouncedEmitModelUpdate.cancel();
});
</script>

<style scoped>
.color-hex {
  font-size: 12px;
  color: #667085;
  font-family: monospace;
}

.full-opacity {
  --tw-text-opacity: 1;
}

.gradient-background__label {
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}
</style>
